import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import noop from '../../utilities/noop';
import Playlist, { removeItems } from '../../models/playlist/playlist';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import { ActivePlaylistContext } from '../../services/activePlaylist/activePlaylist';
import { LibraryContext } from '../../services/library/libraryService';
import {
  SmallDeviceContext,
  SmallDeviceShow,
} from '../../services/smallDevices/smallDeviceService';
import { SyncContext } from '../../services/synchronization/syncService';
import { Context } from '../../services/_globalContext/context';
import Constants from '../../settings';
import ErrorBoundary from '../common/errorBoundary/errorBoundary';
import PlaylistComponent from '../playlist/playlistComponent';
import StartupMessage from '../settingsAndStartup/startupMessage/startupMessage';
import useRoutePath from '../common/hooks/useRoutePath';
import LibraryPlaylists from './libraryPlaylists';

export default function Library() {
  // /app/library/<playlist id>; undefined for the library itself.
  const playlistInUrl = useRoutePath();

  const ctx = useContext(Context);
  const libCtx = useContext(LibraryContext);
  const activePlaylist = useContext(ActivePlaylistContext);
  const syncCtx = useContext(SyncContext);
  const smallDevSvc = useContext(SmallDeviceContext);

  const [curPlaylist, setCurPlaylist] = useState<string>(playlistInUrl || Constants.LibraryId);

  /**
   * A navigation through the router while this page is up - the `g l` and
   * `g n` chords - shows what the url now says.
   *
   * Keyed on the location, not on the playlist parameter: selecting a
   * playlist in the sidebar moves the url with pushState behind the
   * router's back (setCurrentPlaylist below), so the router's parameter is
   * often stale and a push to a url it already believes in would not change
   * it. Every push makes a new location key, whatever the path.
   */
  const location = useLocation();

  useEffect(
    function followRouterNavigation() {
      const target = playlistInUrl || Constants.LibraryId;

      if (target !== curPlaylist) {
        setCurPlaylist(target);
      }
    },
    [location.key],
  );

  /**
   * ctrl+L, this page's half: show the playlist the player asked for. The
   * playlist view then finds the row and completes the request.
   *
   * Reacts to the request and not to the route, because selecting a
   * playlist here moves the url with pushState, behind the router's back -
   * see setCurrentPlaylist - so the router's idea of the current playlist is
   * often stale and a push to a url it already believes in changes nothing.
   *
   * The playlist was there when the player looked. If it went in between
   * the request is closed here, as there will be no view to close it, and
   * a page mounted later must not be yanked to a place nobody asked for.
   */
  useEffect(
    function followLocateRequest() {
      const request = activePlaylist.locateRequest;

      if (!request) {
        return;
      }

      const exists =
        request.playlistId === Constants.LibraryId ||
        request.playlistId === Constants.ActivePlaylistId ||
        libCtx.playlists.some((x) => x.id === request.playlistId);

      if (!exists) {
        activePlaylist.completeLocate(request.id);
        return;
      }

      if (request.playlistId !== curPlaylist) {
        setCurrentPlaylist(request.playlistId);
      }

      // On a phone the playlist list may be covering the tracks.
      smallDevSvc.setState(SmallDeviceShow.Main);
    },
    [activePlaylist.locateRequest],
  );

  /**
   * Opening a synced playlist syncs it - throttled inside syncPlaylist, so
   * flipping between playlists is cheap. Re-run when either service's sign-in
   * arrives, since a selection made before it could do nothing.
   * Deliberately not on every playlist edit; the sync service watches those.
   */
  useEffect(
    function syncSelectedPlaylist() {
      const selected = libCtx.playlists.find((x) => x.id === curPlaylist);

      if (selected && (syncCtx.canSyncYouTube || syncCtx.canSyncSoundCloud)) {
        syncCtx.syncPlaylist(selected);
      }
    },
    [curPlaylist, syncCtx.canSyncYouTube, syncCtx.canSyncSoundCloud],
  );

  function setCurrentPlaylist(val: string) {
    // This way we skip re-rendering the whole tree
    // Re-rendering the context components is not expensive but for some reason this cuts a
    // previously 250ms render to 100ms
    window.history.pushState(undefined, 'Fadila', `/app/library/${val}`);
    // navigate(`/app/library/${val}`);
    setCurPlaylist(val);
  }

  /**
   * The following lines aim to reduce re-rendering for playlist components
   * Previously these methods were being recreated every render causing needless
   * PlaylistComponent updates
   */
  const libPl = libCtx.playlists.find((x) => x.id === curPlaylist);

  const libraryPlaylistRemoveItems = useCallback(
    libPl ? removeItems(libPl, libCtx.updatePlaylist) : noop,
    [libPl, libCtx.updatePlaylist],
  );

  const libraryPlaylistClear = useCallback(
    libPl !== undefined ? () => libCtx.clearPlaylist(libPl) : noop,
    [libPl],
  );

  return (
    <ErrorBoundary>
      <StartupMessage />
      <article className="grid-container full fluid">
        <div className="grid-x">
          <div className="cell shrink">
            <div className={`mainViewport ${ctx.playerSize}`}>
              <LibraryPlaylists curPlaylist={curPlaylist} setCurPlaylist={setCurrentPlaylist} />
            </div>
          </div>
          <div className="cell auto">
            {curPlaylist &&
              (() => {
                let playlist: Playlist | undefined;

                if (curPlaylist === Constants.LibraryId) {
                  var moveable = false;
                  // We check for undefined before allowing user to select library
                  playlist = libCtx.libraryPlaylist!;
                  var remItem = (...itemsToRemove: PlaylistItem[]) => {
                    libCtx.removeFromLibrary(itemsToRemove);
                  };
                  var clrPlaylist = libCtx.resetLibrary;
                  var updatePlaylist = (_pl: Playlist) => {}; // unused
                } else if (curPlaylist === Constants.ActivePlaylistId) {
                  /**
                   * We don't allow active playlist ordering since it's likely to be confusing
                   * Ordering an ap is only a visual action. That is it does not change the play order of tracks
                   */
                  var notOrderable = true;
                  var moveable = true;
                  playlist = activePlaylist.value;
                  var remItem = activePlaylist.removeItems;
                  var clrPlaylist = activePlaylist.clearPlaylist;
                  var updatePlaylist = (pl: Playlist) =>
                    activePlaylist.updateActivePlaylist(pl.tracks);
                } else {
                  var moveable = true;
                  playlist = libCtx.playlists.find((x) => x.id === curPlaylist);
                  var remItem = libraryPlaylistRemoveItems;
                  var clrPlaylist = libraryPlaylistClear;
                  var updatePlaylist = libCtx.updatePlaylist;
                }

                if (playlist) {
                  return (
                    <PlaylistComponent
                      moveable={moveable}
                      notOrderable={notOrderable!}
                      playlist={playlist}
                      updatePlaylist={updatePlaylist}
                      removeItem={remItem}
                      clearPlaylist={clrPlaylist}
                    />
                  );
                }
              })()}
          </div>
        </div>
      </article>
    </ErrorBoundary>
  );
}
